// frontend/src/components/TenantSelector.jsx

import { useState } from "react";
import { setStoredTenantId, getStoredTenantId } from "../utils/tenant.js";

/**
 * Verifica si estamos en modo desarrollo
 */
function isDevelopment() {
  const hostname = window.location.hostname;
  
  // Es desarrollo si es localhost, 127.0.0.1 o una IP privada
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return true;
  }
  
  // Verificar si es una IP (192.168.x.x, 10.x.x.x, etc)
  const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
  return ipPattern.test(hostname);
}

/**
 * Selector de tenant para DESARROLLO
 * Permite cambiar entre diferentes tenants sin cambiar de dominio
 */
export default function TenantSelector({ currentTenant }) {
  const [isOpen, setIsOpen] = useState(false);
  
  const tenants = [
    { id: 'power_training', nombre: 'Power Training', color: '#ea580c' },
    { id: 'fitzone_elite', nombre: 'FitZone Elite', color: '#0284c7' },
    { id: 'iron_warriors', nombre: 'Iron Warriors', color: '#dc2626' },
  ];
  
  const currentTenantId = getStoredTenantId() || 'gimnasio_power';
  const activeTenant = tenants.find(t => t.id === currentTenantId) || tenants[0];
  
  const handleTenantSelect = (tenantId) => {
    
    // Guardar el nuevo tenant
    setStoredTenantId(tenantId);
    setIsOpen(false);
    
    // Limpiar sesión anterior
    localStorage.removeItem('access');
    localStorage.removeItem('userId');
    localStorage.removeItem('isStaff');
    
    // Agregar clase de transición para evitar flickering
    document.documentElement.classList.add('theme-transitioning');
    
    // Recargar la página para aplicar el nuevo tenant
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };
  
  // Solo mostrar en desarrollo
  if (!isDevelopment()) {
    return null;
  }
  
  
  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Botón flotante con el tenant actual */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="px-4 py-3 rounded-full shadow-2xl hover:shadow-3xl transition-all border-2 flex items-center space-x-3"
        style={{
          backgroundColor: activeTenant.color,
          borderColor: activeTenant.color,
          color: 'white'
        }}
      >
        <span className="text-2xl">🏋️</span>
        <div className="text-left">
          <div className="text-xs font-semibold opacity-80">MODO DEV</div>
          <div className="text-sm font-bold">{activeTenant.nombre}</div>
        </div>
        <svg 
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          fill="currentColor" 
          viewBox="0 0 20 20"
        >
          <path 
            fillRule="evenodd" 
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" 
            clipRule="evenodd"
          />
        </svg>
      </button>
      
      {/* Panel de selección */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/20 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div className="absolute bottom-20 right-0 w-80 bg-slate-800 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden">
            <div className="p-4 bg-slate-900 border-b border-slate-700">
              <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold">🔧 Selector de Tenant</p>
              <p className="text-xs text-slate-500 mt-1">Solo visible en desarrollo</p>
            </div>
            
            <div className="p-2">
              {tenants.map((tenant) => {
                const isActive = tenant.id === currentTenantId;
                
                return (
                  <button
                    key={tenant.id}
                    onClick={() => handleTenantSelect(tenant.id)}
                    className={`w-full px-4 py-3 text-left rounded-lg transition-all mb-2 ${
                      isActive 
                        ? 'bg-slate-700 ring-2' 
                        : 'hover:bg-slate-700/50'
                    }`}
                    style={isActive ? { ringColor: tenant.color } : {}}
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: tenant.color }}
                      >
                        {tenant.nombre.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-semibold flex items-center">
                          {tenant.nombre}
                          {isActive && (
                            <span className="ml-2 text-green-400">✓</span>
                          )}
                        </p>
                        <p className="text-xs text-slate-400">{tenant.id}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            
            <div className="p-3 bg-slate-900 border-t border-slate-700 text-xs text-slate-500">
              <p>💡 Al cambiar de tenant:</p>
              <ul className="mt-1 space-y-1 ml-4">
                <li>• Se cierra la sesión actual</li>
                <li>• Cambian los colores y logo</li>
                <li>• Se recargan los datos del nuevo tenant</li>
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
}